import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useMemo, memo } from "react";
import { Card } from "@/core/components/ui/card";
import { cn } from "@/core/utils/utils";
import { REFERRAL_COLUMN } from "@/referrals/constants";
import type { IReferral, ReferralColumn } from "@/referrals/referral";
import {
  ClosedReferralCardContent,
  ClosedReferralCardSkeleton,
} from "./content/closed-referral-card-content";
import {
  InProgressReferralCardContent,
  InProgressReferralCardSkeleton,
} from "./content/in-progress-referral-card-content";
import {
  NewReferralCardContent,
  NewReferralCardSkeleton,
} from "./content/new-referral-card-content";
import {
  ScheduledReferralCardContent,
  ScheduledReferralCardSkeleton,
} from "./content/scheduled-referral-card-content";

// Clean interface without loading state
interface ReferralCardProps {
  referral: IReferral;
  column: ReferralColumn;
  isDragging?: boolean;
  isSelected?: boolean;
  onClick: () => void;
  onNextColumn: (column: ReferralColumn) => void;
  onPayout: () => void;
  onLost?: () => void;
  onMoveToPaid?: () => void;
  onDelete?: () => void;
}

interface ReferralCardSkeletonProps {
  column: ReferralColumn;
  className?: string;
}

export const ReferralCardSkeleton = ({ column }: ReferralCardSkeletonProps) => {
  const SkeletonContent = useMemo(() => {
    if (column === "new") {
      return <NewReferralCardSkeleton />;
    }
    if (column === "inProgress") {
      return <InProgressReferralCardSkeleton />;
    }
    if (column === "scheduled") {
      return <ScheduledReferralCardSkeleton />;
    }
    if (column === "closed") {
      return <ClosedReferralCardSkeleton />;
    }
    return null;
  }, [column]);

  return (
    <Card className={cn("w-full animate-pulse rounded-2xl")}>
      {SkeletonContent}
    </Card>
  );
};

const ReferralCard = memo(
  ({
    referral,
    column,
    isDragging,
    isSelected,
    onClick,
    onNextColumn,
    onPayout,
    onLost,
    onMoveToPaid,
    onDelete,
  }: ReferralCardProps) => {
    const { attributes, listeners, setNodeRef, transform } = useDraggable({
      id: referral.id,
      data: { column },
    });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition: transform ? undefined : "transform 200ms ease",
    };

    const Content = useMemo(() => {
      if (column === "new") {
        return (
          <NewReferralCardContent
            referral={referral}
            onAccept={() => onNextColumn(REFERRAL_COLUMN.inProgress)}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      if (column === "inProgress") {
        return (
          <InProgressReferralCardContent
            referral={referral}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      if (column === "scheduled") {
        return (
          <ScheduledReferralCardContent
            referral={referral}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      if (column === "closed") {
        return (
          <ClosedReferralCardContent
            referral={referral}
            onPayout={onPayout}
            onLost={onLost}
            onMoveToPaid={onMoveToPaid}
            onDelete={onDelete}
          />
        );
      }
      return null;
    }, [column, referral, onLost, onMoveToPaid, onDelete]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (!transform) {
          onClick?.();
        }
      }
    };

    return (
      <Card
        ref={setNodeRef}
        style={style}
        className={cn(
          "w-full rounded-2xl",
          isDragging && "opacity-0",
          "active:cursor-grabbing",
          "focus:border-2 focus:border-ring focus:outline-none",
          isSelected && "border-2 border-ring",
        )}
        onClick={() => {
          if (!transform) {
            onClick?.();
          }
        }}
        data-cy="referral-card"
        {...attributes}
        {...listeners}
        tabIndex={attributes.tabIndex ?? 0}
        onKeyDown={(e) => {
          (
            listeners as { onKeyDown?: (event: React.KeyboardEvent) => void }
          ).onKeyDown?.(e);
          if (e.defaultPrevented) return;
          handleKeyDown(e);
        }}
      >
        {Content}
      </Card>
    );
  },
);

export default ReferralCard;
